import { produce } from "immer";
import { ActionType, createReducer } from "typesafe-actions";

import { ChatsFilter, ChatStateType } from "../interfaces";
import * as actions from "./actions";

type Action = ActionType<typeof actions>;

const defaultFilter: ChatsFilter = {
  page: 0,
  limit: 50,
};

export const initialState: ChatStateType = {
  chats: [],
  chatsTotal: 0,
  activeChat: null,
  starters: [],
  quota: null,
  filter: { ...defaultFilter },
  reactionFailureCount: 0,
  lastReactedMessage: null,
};

const reducer = createReducer<ChatStateType, Action>(initialState)
  .handleAction(actions.getChatList.success, (state, action) =>
    produce(state, (nextState) => {
      const { results, total, is_clear } = action.payload;
      nextState.chats = !is_clear ? [...nextState.chats, ...results] : [...results];
      nextState.chatsTotal = total;
    }),
  )
  .handleAction(actions.getChat.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.activeChat = action.payload;

      if (!nextState.lastReactedMessage || !nextState.activeChat) return;

      // phase-f4 (3): message uuids are unique to their OWN chat -- if the incoming chat doesn't
      // carry this message at all, lastReactedMessage belongs to a DIFFERENT chat than the one this
      // fetch just loaded (this tab navigated away and back, or to somewhere else entirely). Drop
      // it, or a LATER getChat.success for that other chat could coincidentally find a message at
      // the same array index and overlay an unrelated reaction onto it (mutation: never reset).
      const reactedIndex = nextState.activeChat.messages.findIndex(
        (m) => m.uuid === nextState.lastReactedMessage?.uuid,
      );

      if (reactedIndex === -1) {
        nextState.lastReactedMessage = null;
        return;
      }

      // phase-f4 (2): a stale getChat.success can resolve AFTER reactToMessage.success and overwrite
      // the reaction with the pre-reaction copy it fetched -- lastReactedMessage is this tab's own
      // latest reaction intent, so it overlays onto the matching message here rather than losing to
      // whatever this fetch happened to see (mutation: no overlay).
      const { is_liked, feedback, feedback_reason } = nextState.lastReactedMessage;
      nextState.activeChat.messages[reactedIndex] = {
        ...nextState.activeChat.messages[reactedIndex],
        is_liked,
        feedback,
        feedback_reason,
      };
    }),
  )
  .handleAction(actions.updateChat.success, (state, action) =>
    produce(state, (nextState) => {
      const chats = nextState.chats;
      const foundIndex = chats.findIndex((c) => c.uuid === action.payload.uuid);

      if (foundIndex !== -1) {
        chats[foundIndex] = { ...chats[foundIndex], title: action.payload.title };
      }
      nextState.chats = [...chats];

      // Patch only the title, don't replace the whole object: the rename response's `messages` is
      // bounded to the 10 most recent (backend's `messages(recent)`), so a full swap would truncate
      // an already-loaded longer conversation.
      if (nextState.activeChat && nextState.activeChat.uuid === action.payload.uuid) {
        nextState.activeChat.title = action.payload.title;
      }
    }),
  )
  .handleAction(actions.deleteChat.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.chats = nextState.chats.filter((c) => c.uuid !== action.payload.uuid);
      nextState.chatsTotal = nextState.chatsTotal - 1;
      if (state.activeChat && state.activeChat.uuid === action.payload.uuid) {
        nextState.activeChat = null;
      }
    }),
  )
  .handleAction(actions.reactToMessage.success, (state, action) =>
    produce(state, (nextState) => {
      // phase-f3: recorded UNCONDITIONALLY, before the activeChat-patch attempt below -- a reaction
      // can land before the message has ever reached activeChat (no fetch yet, or a stale one still
      // in flight), and useChatMessages resolves a pending reaction from this value as a second,
      // independent source when that happens (mutation: only set when found in activeChat).
      nextState.lastReactedMessage = action.payload;

      if (!nextState.activeChat) return;

      const messages = nextState.activeChat.messages;
      const foundIndex = messages.findIndex((m) => m.uuid === action.payload.uuid);

      if (foundIndex !== -1) {
        messages[foundIndex] = action.payload;
        nextState.activeChat.messages = [...messages];
      }
    }),
  )
  .handleAction(actions.reactToMessage.failure, (state) =>
    produce(state, (nextState) => {
      // Monotonic tick only — useChatMessages watches this to clear a pending reaction's
      // disabled state on failure, since (unlike .success) a failure has nothing else in
      // the store to patch. Never touched by .request/.success, or the watch-effect would
      // fire spuriously and clear the pending state the instant a reaction starts.
      nextState.reactionFailureCount += 1;
    }),
  )
  .handleAction(actions.getStarters.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.starters = action.payload;
    }),
  )
  .handleAction(actions.getQuota.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.quota = action.payload.quota;
    }),
  )
  .handleAction(actions.setFilter, (state, action) =>
    produce(state, (nextState) => {
      nextState.filter = action.payload || { ...defaultFilter };
    }),
  )
  .handleAction(actions.clearChat, (state) =>
    produce(state, (nextState) => {
      nextState.activeChat = null;
      // phase-f4 (3): a reaction belonging to the chat we're leaving must not resurface (or get
      // overlaid onto some other chat's message later) once we're looking at nothing / a new chat.
      nextState.lastReactedMessage = null;
    }),
  )
  .handleAction(actions.clearChats, (state) =>
    produce(state, (nextState) => {
      nextState.chats = [];
      nextState.chatsTotal = 0;
    }),
  );

export { reducer as ChatReducer };
